/**
 * Runtime platform config (read by every module that needs platform-level
 * settings: RPC URL, server URL, Clerk publishable key, etc.).
 *
 * Source of truth: /config.json (admin-managed, on the backend host).
 * The backend writes the public-safe portion to public/runtime-config.js
 * on startup, which is loaded as a <script> in public/index.html BEFORE
 * the React bundle, so values are available synchronously here.
 *
 * Falls back to env vars (REACT_APP_*) and then to hard-coded ETH defaults
 * so the app still boots if the runtime file is missing.
 */

const HARD_DEFAULTS = {
  clerkPublishableKey: '',
  serverUrl: 'http://localhost:3001',
  rpcUrl: 'https://ethereum-rpc.publicnode.com',
  chainId: 1,
  nativeSymbol: 'ETH',
  uniswapV2Router: '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
  wethAddress: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
  // Disperse.app (Ethereum mainnet) — batch native transfers in one tx
  disperseAddress: '0xD152f549545093347A162Dce210e7293f1452150',
  // Bundle relay controls (Enable + Bundle, fund/refund bundles, etc.)
  bundleRelay: 'blockrazor', // 'blockrazor' | 'flashbots' | 'disabled'
  flashbotsUrl: 'https://relay.flashbots.net',
  blockrazorUrl: 'https://eth.blockrazor.xyz',
};

const ENV_OVERRIDES = {
  clerkPublishableKey: process.env.REACT_APP_CLERK_PUBLISHABLE_KEY,
  serverUrl: process.env.REACT_APP_SERVER_URL,
  rpcUrl: process.env.REACT_APP_RPC_URL,
  chainId: process.env.REACT_APP_CHAIN_ID ? Number(process.env.REACT_APP_CHAIN_ID) : undefined,
  nativeSymbol: process.env.REACT_APP_NATIVE_SYMBOL,
  uniswapV2Router: process.env.REACT_APP_ROUTER_ADDRESS,
  wethAddress: process.env.REACT_APP_NATIVE_COIN,
  disperseAddress: process.env.REACT_APP_DISPERSE_ADDRESS,
  bundleRelay: process.env.REACT_APP_BUNDLE_RELAY,
  flashbotsUrl: process.env.REACT_APP_FLASHBOTS_URL,
  blockrazorUrl: process.env.REACT_APP_BLOCKRAZOR_URL,
};

function pickDefined(...candidates) {
  for (const v of candidates) {
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

function buildRuntimeConfig() {
  const fromWindow =
    (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
  const merged = {};
  for (const key of Object.keys(HARD_DEFAULTS)) {
    /**
     * `serverUrl: ""` means same-origin API (Fly / single-host deploy). Older
     * pickDefined skipped empty strings and incorrectly fell through to
     * http://localhost:3001 → browser "Failed to fetch" on HTTPS sites.
     */
    if (
      key === 'serverUrl' &&
      typeof fromWindow.serverUrl === 'string' &&
      fromWindow.serverUrl.trim() === ''
    ) {
      merged[key] = '';
      continue;
    }
    merged[key] = pickDefined(fromWindow[key], ENV_OVERRIDES[key], HARD_DEFAULTS[key]);
  }
  return merged;
}

const runtimeConfig = buildRuntimeConfig();

export function getRuntimeConfig() {
  return runtimeConfig;
}

export default runtimeConfig;
